import { useState, useEffect, useMemo } from "react";
import { StatusBadge } from "../../../components/StatusBadge";
import Layout from "../../../components/Layout";
import { Progress } from "flowbite-react";
import { HiPlus, HiX, HiPencil, HiSearch } from "react-icons/hi";
import EmployeeLoanForm from "./EmployeeLoanForm";
import { CustomTable, CustomTableHead, CustomTableBody, CustomTableHeadCell, CustomTableRow, CustomTableCell } from "../../../components/CustomTable";
import { CustomButton, CustomInput } from "../../../components/FormFields";
import { fetchLoans, createLoan, updateLoan, closeLoan } from "../../../services/loanService";

const statusColor = { Active: "warning", Closed: "success" };

export default function EmployeeLoan() {
  const [loans, setLoans]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);   // the loan being edited, null when granting
  const [saving, setSaving]     = useState(false);
  const [query, setQuery]       = useState("");

  const load = async () => {
    setLoading(true);
    try { const d = await fetchLoans(); setLoans(Array.isArray(d) ? d : []); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (data) => {
    // one save at a time - a double click on Grant Loan gave emp 789 the same
    // insurance loan twice, 0.24 seconds apart
    if (saving) return;
    setSaving(true);
    try {
      if (editing) await updateLoan(editing.id, data);
      else await createLoan(data);
      closeDialog();
      load();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const openEdit = (l) => { setEditing(l); setShowForm(true); };
  const closeDialog = () => { setShowForm(false); setEditing(null); };

  const handleClose = async (id) => {
    if (!window.confirm("Close this loan?")) return;
    try { await closeLoan(id); load(); } catch (err) { console.error(err); }
  };

  const active = loans.filter((l) => l.status === "Active");

  // the EPF number is what Nadee works from, so it is both the search and the order
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return loans
      .filter((l) => !q || String(l.emp_id).toLowerCase().includes(q)
        || String(l.empName || "").toLowerCase().includes(q))
      .sort((a, b) => (Number(a.emp_id) - Number(b.emp_id)) || (a.id - b.id));
  }, [loans, query]);

  // what each kind of loan recovers a month, so insurance can be checked
  // against the insurer's list and festival advances against the register
  const byType = Object.values(active.reduce((acc, l) => {
    const k = l.loanType || "Other";
    acc[k] ??= { type: k, count: 0, monthly: 0, outstanding: 0 };
    acc[k].count += 1;
    acc[k].monthly += Number(l.monthlyInstallment || 0);
    acc[k].outstanding += Number(l.remaining || 0);
    return acc;
  }, {})).sort((a, b) => b.monthly - a.monthly);

  return (
    <Layout>
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Employee Loans</h1>
            <p className="mt-1 text-sm text-gray-500">Grant loans, track installments and manage balances</p>
          </div>
          <CustomButton color="purple" onClick={() => setShowForm(true)}>
            <HiPlus className="mr-2 h-4 w-4" /> Grant Loan
          </CustomButton>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[
            { label: "Active Loans",      value: active.length, color: "text-orange-600" },
            { label: "Total Outstanding", value: `Rs. ${active.reduce((s, l) => s + Number(l.remaining), 0).toLocaleString()}`, color: "text-red-600" },
            { label: "Monthly Recovery",  value: `Rs. ${active.reduce((s, l) => s + Number(l.monthlyInstallment), 0).toLocaleString()}`, color: "text-green-600" },
          ].map(({ label, value, color }) => (
            <div key={label} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <p className="text-sm text-gray-500">{label}</p>
              <p className={`mt-1 text-2xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>

        {byType.length > 0 && (
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-gray-500">Active loans by type</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {byType.map((t) => (
                <div key={t.type} className="rounded-lg bg-gray-50 px-4 py-3 dark:bg-gray-900/40">
                  <p className="truncate text-sm font-semibold text-gray-700 dark:text-gray-200">{t.type}</p>
                  <p className="mt-1 text-xs text-gray-500">{t.count} staff · Rs. {t.monthly.toLocaleString()} / month</p>
                  <p className="text-xs text-gray-400">Rs. {t.outstanding.toLocaleString()} outstanding</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="border-b border-gray-100 px-5 py-3 dark:border-gray-700">
            <div className="max-w-xs">
              <CustomInput icon={HiSearch} value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by EPF number or name…" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <CustomTable hoverable>
              <CustomTableHead>
                <CustomTableRow>
                  <CustomTableHeadCell>EPF No</CustomTableHeadCell>
                  <CustomTableHeadCell>Employee</CustomTableHeadCell>
                  <CustomTableHeadCell>Loan Type</CustomTableHeadCell>
                  <CustomTableHeadCell>Principal</CustomTableHeadCell>
                  <CustomTableHeadCell>Interest</CustomTableHeadCell>
                  <CustomTableHeadCell>Total Payable</CustomTableHeadCell>
                  <CustomTableHeadCell>Monthly</CustomTableHeadCell>
                  <CustomTableHeadCell>Progress</CustomTableHeadCell>
                  <CustomTableHeadCell>Status</CustomTableHeadCell>
                  <CustomTableHeadCell className="text-center">Actions</CustomTableHeadCell>
                </CustomTableRow>
              </CustomTableHead>
              <CustomTableBody className="divide-y divide-gray-100 dark:divide-gray-700">
                {loading ? (
                  <CustomTableRow><CustomTableCell colSpan={10} className="py-8 text-center text-sm text-gray-400">Loading…</CustomTableCell></CustomTableRow>
                ) : shown.map((l) => {
                  const total    = Number(l.totalPayable);
                  const remain   = Number(l.remaining);
                  const paidPct  = total > 0 ? Math.round(((total - remain) / total) * 100) : 0;
                  return (
                    <CustomTableRow key={l.id} className="hover:bg-purple-50 dark:hover:bg-gray-700">
                      <CustomTableCell className="font-mono text-sm text-purple-500">{l.emp_id}</CustomTableCell>
                      <CustomTableCell className="whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">{l.empName}</CustomTableCell>
                      <CustomTableCell className="text-sm text-gray-600 dark:text-gray-300">{l.loanType}</CustomTableCell>
                      <CustomTableCell className="text-sm font-semibold text-gray-700 dark:text-gray-200">Rs. {Number(l.principal).toLocaleString()}</CustomTableCell>
                      <CustomTableCell><StatusBadge color={Number(l.interestRate) === 0 ? "success" : "warning"} className="w-fit text-xs">{l.interestRate}%</StatusBadge></CustomTableCell>
                      <CustomTableCell className="text-sm text-gray-700 dark:text-gray-200">Rs. {total.toLocaleString()}</CustomTableCell>
                      <CustomTableCell className="text-sm text-gray-700 dark:text-gray-200">Rs. {Number(l.monthlyInstallment).toLocaleString()}</CustomTableCell>
                      <CustomTableCell className="min-w-[140px]">
                        <Progress progress={paidPct} size="sm" color="purple" />
                        <p className="mt-1 text-xs text-gray-400">{paidPct}% paid</p>
                      </CustomTableCell>
                      <CustomTableCell><StatusBadge color={statusColor[l.status]} className="w-fit text-xs">{l.status}</StatusBadge></CustomTableCell>
                      <CustomTableCell>
                        <div className="flex justify-center gap-2">
                          {l.status === "Active" && (<>
                            <CustomButton size="xs" color="purple" outline pill title="Edit Loan" onClick={() => openEdit(l)}>
                              <HiPencil className="h-3.5 w-3.5" />
                            </CustomButton>
                            <CustomButton size="xs" color="failure" outline pill title="Close Loan" onClick={() => handleClose(l.id)}>
                              <HiX className="h-3.5 w-3.5" />
                            </CustomButton>
                          </>)}
                        </div>
                      </CustomTableCell>
                    </CustomTableRow>
                  );
                })}
                {!loading && shown.length === 0 && (
                  <CustomTableRow><CustomTableCell colSpan={10} className="py-8 text-center text-sm text-gray-400">
                    {query ? `No loan matches “${query}”.` : "No loans found."}
                  </CustomTableCell></CustomTableRow>
                )}
              </CustomTableBody>
            </CustomTable>
          </div>
        </div>
      </div>
      {showForm && (
        <EmployeeLoanForm key={editing?.id ?? "new"} loan={editing}
          closeForm={closeDialog} onSave={handleSave} saving={saving} />
      )}
    </Layout>
  );
}
